import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { check, nullable, object, pipe, string } from "valibot";
import {
  dataName,
  errorTranslation,
  integerValue,
  label,
  optionalTextValue,
  rememberInput,
  required,
} from "../../utils/validation/base";
import { useFormServices } from "../../services/formServices";
import { TextInput } from "../../components/inputs/TextInput";
import { NumberInput } from "../../components/inputs/NumberInput";
import { isEmpty } from "lodash-es";
import { useTranslation } from "../../state/i18n";
import { MAX_CHARACTERS_SM, MAX_NUMBER_SM, TEXT_CONDITION_KEYS } from "../../constants";
import { defaultStringFilter } from "../../pages/customer/form/filter";
import { Toggle } from "../../components/toggle/Toggle";
import { InputPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { createMemo } from "solid-js";
import GlobalErrorInlineMessage from "../GlobalErrorInlineMessage";
import { keyFromSelector } from "i18next";

const Schema = pipe(
  object({
    label_text: label(),
    text_placeholder: optionalTextValue(MAX_CHARACTERS_SM),
    text_default_value: optionalTextValue(MAX_CHARACTERS_SM),
    min_length: nullable(integerValue(0, MAX_CHARACTERS_SM)),
    max_length: nullable(integerValue(0, MAX_CHARACTERS_SM)),
    data_name: dataName(),
    required: required(),
    remember_input: rememberInput(),
  }),
  check(
    ({ min_length, max_length }) => (max_length ?? MAX_CHARACTERS_SM) >= (min_length ?? 0),
    errorTranslation(keyFromSelector(($) => $.VALIDATION_TEXT_MAX_GREATER_EQUALS_MIN)),
  ),
  check(
    ({ min_length, max_length, text_default_value }) =>
      isEmpty(text_default_value)
        ? true
        : text_default_value!.length >= (min_length ?? 0) &&
          text_default_value!.length <= (max_length ?? MAX_CHARACTERS_SM),
    errorTranslation(keyFromSelector(($) => $.VALIDATION_TEXT_DEFAULT_OUTSIDE_MIN_MAX)),
  ),
);
const ValueSchema = string();

export type TextFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const TextField: Field<TextFieldProperties, DefaultDtoType, ValueType> = {
  type: "text",
  category: "textFields",
  widget: "com.moreapps:text:1",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    text_placeholder: "",
    text_default_value: "",
    max_length: null,
    min_length: null,
    required: false,
    remember_input: false,
  }),
  ruleBehaviour: {
    conditions: { keys: TEXT_CONDITION_KEYS, variant: "text" },
    actions: {
      variant: "text",
      setValue: {
        fieldTypes: [
          "address",
          "barcode",
          "checkbox",
          "calculation",
          "currentLocation",
          "date",
          "dateTime",
          "email",
          "iban",
          "licensePlate",
          "lookup",
          "number",
          "phone",
          "price",
          "radio",
          "rating",
          "readOnlyText",
          "search",
          "slider",
          "stopwatch",
          "text",
          "textArea",
          "time",
          "timeDifference",
        ],
        variableTypes: ["string", "number", "boolean", "date", "datetime"],
      },
    },
  },
  filter: {
    type: "string",
    definition: defaultStringFilter,
  },
  stringValue: (value) => value,
  renderValue: (value) => value,
  icon: "textIcon",
  titleKey: keyFromSelector(($) => $.TEXT_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.TEXT_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    text_placeholder: keyFromSelector(($) => $.FIELD_PROPERTY_PLACEHOLDER),
    text_default_value: keyFromSelector(($) => $.FIELD_PROPERTY_DEFAULT_VALUE),
    max_length: keyFromSelector(($) => $.FIELD_PROPERTY_MAX_LENGTH),
    min_length: keyFromSelector(($) => $.FIELD_PROPERTY_MIN_LENGTH),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <InputPreview placeholder={props.properties.text_placeholder} value={props.properties.text_default_value} />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();
    const globalErrors = createMemo(() => props.getGlobalErrors());

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            onInput={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
          <TextInput
            label={t(props.propertyKeys.text_placeholder)}
            value={props.data.properties.text_placeholder!}
            onBlur={(e) => props.setValue("text_placeholder", e.target.value)}
            errorMessage={props.getError("text_placeholder")}
          />
          <TextInput
            label={t(props.propertyKeys.text_default_value)}
            value={props.data.properties.text_default_value!}
            onBlur={(e) => props.setValue("text_default_value", e.target.value)}
            errorMessage={props.getError("text_default_value")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
          <NumberInput
            label={t(props.propertyKeys.min_length)}
            value={props.data.properties.min_length}
            minValue={0}
            maxValue={MAX_NUMBER_SM}
            onBlur={(value) => props.setValue("min_length", value)}
            errorMessage={props.getError("min_length")}
          />
          <NumberInput
            label={t(props.propertyKeys.max_length)}
            value={props.data.properties.max_length}
            minValue={0}
            maxValue={MAX_NUMBER_SM}
            onBlur={(value) => props.setValue("max_length", value)}
            errorMessage={props.getError("max_length")}
          />
          <GlobalErrorInlineMessage errors={globalErrors()} />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(TextField);
